import { AttachmentFile } from '@billy/design-system'
import qs from 'qs'

import { BankPayment } from '@features/transactions/types/bankPayment'
import { Posting } from '@modules-deprecated/bankReconciliation/types'
import { BalanceModifier } from '@modules-deprecated/inbox/types'

import { Metable } from '../../types/metable'
import { getCurrentLocale, getRequest, postRequest, putRequest } from '../../utils'
import { Contact } from '../contacts/types/contact'
import { Transaction } from '../transactions/types/transaction'
import { Bill } from './types/bill'
import { BillAttachment } from './types/billAttachment'
import { BillLine } from './types/billLine'

export interface GetBillPayload {
  billId: string
  include?: string
}

export interface GetBillResponseData {
  bill: Bill
  billLines: Partial<BillLine>[]
  contacts?: Contact[]
}

export const getBill = ({
  billId,
  include = 'bill.contact,bill.attachments:embed,attachment.file:embed,file.variants:embed,bill.lines,bill.balanceModifiers.balanceModifier.modifier:embed,bill.integrationStatus:embed',
}: GetBillPayload): Promise<GetBillResponseData> => {
  const locale = getCurrentLocale()
  const queryParams = qs.stringify({
    ...(include ? { include } : {}),
  })

  return getRequest(`/v2/bills/${billId}?${queryParams}`, { 'accept-language': locale })
}

export interface CreateBillPayload extends Partial<Omit<Bill, 'attachments'>> {
  organizationId: string
  attachments?: Partial<BillAttachment>[]
}

export interface CreateBillResponseData {
  bills: Bill[]
  billLines?: BillLine[]
  attachments: BillAttachment[]
  files?: AttachmentFile[]
}

export const createBill = (payload: CreateBillPayload): Promise<CreateBillResponseData> => {
  const locale = getCurrentLocale()
  return postRequest('/v2/bills', { bill: payload }, { 'accept-language': locale })
}

export interface UpdateBillPayload {
  billId: string
  payload: CreateBillPayload
}

export type UpdateBillResponseData = CreateBillResponseData

export const updateBill = ({ billId, payload }: UpdateBillPayload): Promise<UpdateBillResponseData> => {
  const locale = getCurrentLocale()
  return putRequest(`/v2/bills/${billId}`, { bill: payload }, { 'accept-language': locale })
}

interface BillPaymentPayload {
  id: string
  isVoided: boolean
}

export interface VoidBillPaymentPayload {
  paymentId: string
  payload: BillPaymentPayload
}

export interface VoidBillPaymentResponseData extends Metable {
  balanceModifiers: BalanceModifier[]
  bankPayments: BankPayment[]
  bills: Bill[]
  postings: Posting[]
  transactions: Transaction[]
}

export const voidBillPayment = ({
  paymentId,
  payload,
}: VoidBillPaymentPayload): Promise<VoidBillPaymentResponseData> => {
  const locale = getCurrentLocale()
  return putRequest(`/v2/bankPayments/${paymentId}`, { bankPayment: payload }, { 'accept-language': locale })
}
